import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useAuth } from "@/context/AuthContext";
import { Calendar, CurrencyEur, CheckCircle, ArrowSquareOut, Clock, WarningCircle, Bell, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  open: { label: "Open", bg: "bg-[#ECFDF5]", text: "text-[#059669]", border: "border-[#059669]" },
  closing_soon: { label: "Closing Soon", bg: "bg-[#FEF3C7]", text: "text-[#B45309]", border: "border-[#F59E0B]" },
  closed: { label: "Closed", bg: "bg-[#FEF2F2]", text: "text-[#DC2626]", border: "border-[#E0D4C8]" },
  upcoming: { label: "Opening Soon", bg: "bg-[#EFF6FF]", text: "text-[#1D4ED8]", border: "border-[#1D4ED8]" },
  participants_only: { label: "Participants Only", bg: "bg-[#F6E9DD]", text: "text-[#6B6661]", border: "border-[#E0D4C8]" },
};

const Countdown = ({ grant }) => {
  if (grant.days_left === null || grant.days_left === undefined) return null;
  if (grant.days_left < 0) return null;
  const urgent = grant.days_left <= 30;
  return (
    <div className={`flex items-center gap-1.5 ${urgent ? "text-[#B45309]" : "text-[#0F766E]"}`} data-testid={`grant-countdown-${grant.id}`}>
      <Clock size={14} weight="bold" />
      <span className="font-mono text-sm font-semibold">
        {grant.days_left === 0 ? "Deadline today!" : `${grant.days_left} days left`}
      </span>
    </div>
  );
};

export default function GrantsPage() {
  const { user, token } = useAuth();
  const [grants, setGrants] = useState([]);
  const [sourceNote, setSourceNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [tracked, setTracked] = useState([]);

  useEffect(() => {
    axios.get(`${API}/grants`)
      .then((res) => { setGrants(res.data.grants); setSourceNote(res.data.source_note); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fetchTracked = useCallback(() => {
    if (!token) return;
    axios.get(`${API}/grants/tracked`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setTracked(res.data.grant_ids))
      .catch(() => {});
  }, [token]);

  useEffect(() => { fetchTracked(); }, [fetchTracked]);

  const toggleTrack = async (grant) => {
    if (!token) { toast.error("Sign in to get deadline reminders"); return; }
    const isTracked = tracked.includes(grant.id);
    try {
      if (isTracked) {
        await axios.delete(`${API}/grants/${grant.id}/track`, { headers: { Authorization: `Bearer ${token}` } });
        setTracked(tracked.filter((id) => id !== grant.id));
        toast.success(`Stopped tracking ${grant.scheme}`);
      } else {
        await axios.post(`${API}/grants/${grant.id}/track`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setTracked([...tracked, grant.id]);
        toast.success(`We'll email you a week before the ${grant.scheme} deadline`);
      }
    } catch { toast.error("Failed to update tracking"); }
  };

  const filtered = filter === "all" ? grants : grants.filter((g) => {
    if (filter === "open") return ["open", "closing_soon"].includes(g.status);
    return g.status === filter;
  });

  const counts = {
    all: grants.length,
    open: grants.filter((g) => ["open", "closing_soon"].includes(g.status)).length,
    upcoming: grants.filter((g) => g.status === "upcoming").length,
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SEO title="Farm Grants & Scheme Deadlines" description="Track ACRES, TAMS 3, BISS/CAP, SCEP and ANC deadlines with plain-English eligibility notes for Irish farmers." url="/grants" />
      <Header />
      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-1 bg-[#0F766E] text-white text-xs font-mono uppercase tracking-wider">Grants Tracker</span>
            <span className="px-2 py-1 bg-[#F6E9DD] text-[#6B6661] text-xs font-mono uppercase tracking-wider">🇮🇪 Irish Schemes</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#111827] mb-2">Farm Scheme Deadlines</h1>
          <p className="text-[#6B6661] font-sans max-w-2xl">ACRES, TAMS, CAP and more — what's open, what's closing, and whether you qualify, in plain English.</p>
        </div>

        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {[["all", `All (${counts.all})`], ["open", `Open Now (${counts.open})`], ["upcoming", `Opening Soon (${counts.upcoming})`], ["closed", "Closed"]].map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)} data-testid={`grants-filter-${key}`}
              className={`px-4 py-2 text-sm font-sans transition-colors ${filter === key ? "bg-[#0F766E] text-white" : "bg-white border border-[#E0D4C8] text-[#6B6661] hover:border-[#0F766E]"}`}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid gap-4">{[1, 2, 3].map((i) => <div key={i} className="bg-white border border-[#E0D4C8] h-48 animate-pulse" />)}</div>
        ) : (
          <div className="grid gap-5" data-testid="grants-list">
            {filtered.map((g) => {
              const style = STATUS_STYLES[g.status] || STATUS_STYLES.open;
              return (
                <div key={g.id} className={`bg-white border-l-4 ${style.border} border border-[#E0D4C8]`} data-testid={`grant-card-${g.id}`}>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h2 className="font-serif text-xl text-[#111827]">{g.scheme}</h2>
                          <span className={`px-2 py-0.5 ${style.bg} ${style.text} text-[10px] font-mono uppercase tracking-wide`} data-testid={`grant-status-${g.id}`}>{style.label}</span>
                        </div>
                        <p className="text-sm text-[#6B6661] mt-0.5">{g.full_name}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Countdown grant={g} />
                        <button onClick={() => toggleTrack(g)} data-testid={`track-grant-${g.id}`}
                          title={tracked.includes(g.id) ? "Stop reminders" : "Email me a week before the deadline"}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${tracked.includes(g.id) ? "bg-[#0F766E] text-white" : "bg-white border border-[#0F766E] text-[#0F766E] hover:bg-[#F0FDF4]"}`}>
                          {tracked.includes(g.id) ? <><BellRinging size={13} weight="fill" /> Tracking</> : <><Bell size={13} /> Remind Me</>}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-start gap-2 mb-2">
                      <Calendar size={16} className="text-[#0F766E] mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-[#33302E]">{g.deadline_label}</p>
                    </div>
                    <div className="flex items-start gap-2 mb-4">
                      <CurrencyEur size={16} className="text-[#0F766E] mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-[#33302E]">{g.payment}</p>
                    </div>

                    <div className="bg-[#F6FBF9] border border-[#D1FAE5] p-4 mb-3">
                      <p className="text-[10px] font-mono uppercase tracking-wider text-[#0F766E] mb-2">Do I qualify? — plain English</p>
                      <ul className="space-y-1.5">
                        {(g.eligibility_plain || []).map((point, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-[#33302E]">
                            <CheckCircle size={15} className="text-[#059669] mt-0.5 flex-shrink-0" weight="fill" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <p className="text-sm text-[#6B6661] mb-3"><span className="font-semibold text-[#33302E]">How to apply:</span> {g.how_to_apply}</p>

                    <a href={g.link} target="_blank" rel="noopener noreferrer" data-testid={`grant-link-${g.id}`}
                      className="inline-flex items-center gap-1.5 text-sm text-[#0F766E] font-semibold hover:underline">
                      Official scheme page on gov.ie <ArrowSquareOut size={14} />
                    </a>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && <p className="text-center text-[#6B6661] py-12">No schemes match this filter.</p>}
          </div>
        )}

        {sourceNote && (
          <div className="mt-8 flex items-start gap-2 bg-[#FEF3C7] border border-[#FCD34D] px-4 py-3">
            <WarningCircle size={16} className="text-[#B45309] mt-0.5 flex-shrink-0" />
            <p className="text-xs text-[#92400E]">{sourceNote}</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
